import type { ReactNode } from "react";
import type { Message } from "@langchain/langgraph-sdk";
import { useRecommendations } from "./RecommendationsContext";
import type { CollectedValues } from "./types";

/**
 * The transcript anchor for a submitted recommendation.
 *
 * A panel submission is ONE HumanMessage; its `additional_kwargs.metadata`
 * carries `{ recommendation_id, component, collected }` — self-contained,
 * persisted in LangGraph state, so the rich view re-renders from history
 * without the (ephemeral) recommendations channel. This mirrors the
 * ToolCall→Artifact pairing (e.g. KeywordsResearch) WITHOUT synthesizing
 * tool calls: the artifact is anchored to the human message the user
 * actually sent.
 *
 * Render rule: a bespoke `Submission` renderer wins (by component, then
 * recommendationId); any other submission renders the Root's
 * `defaultSubmission` card — panel submissions mirror their fields, and a
 * prompt chip's send shows the engineered prompt under the chip's title.
 * Only messages WITHOUT a recommendation_id stay plain bubbles.
 */

export interface RecommendationSubmissionInfo {
  recommendationId: string;
  component: string | null;
  collected: CollectedValues;
  /** The freeform note the user attached to the panel submission. */
  note: string | null;
}

/** Read a message's recommendation-submission metadata, if any. */
export function getRecommendationSubmission(
  message: Message,
): RecommendationSubmissionInfo | null {
  const kwargs = (message as { additional_kwargs?: Record<string, unknown> })
    .additional_kwargs;
  const metadata = kwargs?.metadata as Record<string, unknown> | undefined;
  const recommendationId = metadata?.recommendation_id;
  if (typeof recommendationId !== "string" || recommendationId === "") return null;
  return {
    recommendationId,
    component: typeof metadata?.component === "string" ? metadata.component : null,
    collected: (metadata?.collected as CollectedValues | undefined) ?? {},
    note: typeof metadata?.note === "string" ? metadata.note : null,
  };
}

export interface RecommendationSubmissionMessageProps {
  message: Message;
  /**
   * Rendered when the message is not a recommendation submission, or no
   * Submission view is registered for it — typically the surface's normal
   * `<Chat.UserMessage>`.
   */
  fallback?: ReactNode;
}

export function RecommendationSubmissionMessage({
  message,
  fallback,
}: RecommendationSubmissionMessageProps) {
  const { renderers, defaultSubmission } = useRecommendations();
  const submission = getRecommendationSubmission(message);
  const descriptor = submission
    ? (submission.component ? renderers[submission.component] : undefined) ??
      renderers[submission.recommendationId]
    : undefined;

  const Submission = descriptor?.Submission ?? defaultSubmission;

  if (!submission || !Submission) return <>{fallback ?? null}</>;

  return (
    <Submission
      message={message}
      recommendationId={submission.recommendationId}
      component={submission.component}
      collected={submission.collected}
    />
  );
}
